import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Trash2, Plus, FileText, FileEdit, MessageSquare, Send, Mail } from "lucide-react";
import { api, formatApiError } from "../../api";
import DevisCreateModal, { downloadDevisPdf } from "./DevisCreateModal";

const STATUS_META = {
  brouillon: { label: "Brouillon", cls: "bg-white/5 text-white/60 border-white/10" },
  envoye: { label: "Envoyé", cls: "bg-blue-500/10 text-blue-300 border-blue-500/30" },
  accepte: { label: "Accepté", cls: "bg-green-500/10 text-green-300 border-green-500/30" },
  refuse: { label: "Refusé", cls: "bg-red-500/10 text-red-300 border-red-500/30" },
};

function money(n) {
  return `${Number(n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
}
function fmt(iso) { try { return new Date(iso).toLocaleDateString("fr-FR"); } catch { return ""; } }

export default function DevisManager() {
  const [items, setItems] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [creatingFor, setCreatingFor] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [d, q] = await Promise.all([
        api.get("/admin/devis"),
        api.get("/admin/quotes"),
      ]);
      setItems(d.data);
      setQuotes(q.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    await api.patch(`/admin/devis/${id}`, { status });
    toast.success("Statut mis à jour"); load();
  };
  const remove = async (id) => {
    if (!window.confirm("Supprimer ce devis ?")) return;
    await api.delete(`/admin/devis/${id}`); toast.success("Supprimé"); load();
  };
  const sendEmail = async (id) => {
    try { await api.post(`/admin/devis/${id}/send`); toast.success("Email envoyé au client"); load(); }
    catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  const pending = quotes.filter((q) => q.status !== "devis_envoye" && q.status !== "clos");

  return (
    <div className="space-y-6" data-testid="devis-manager">
      <div className="rounded-lg border border-white/10 bg-[#0f0f0f]">
        <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
          <div>
            <h3 className="flex items-center gap-2 font-head text-lg font-medium uppercase tracking-tight text-white">
              <MessageSquare size={16} className="text-[#ff4d4d]" />
              Demandes en attente
            </h3>
            <p className="text-xs text-white/50">{pending.length} demande(s) sans devis</p>
          </div>
          <button onClick={() => setCreateOpen(true)} className="inline-flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 text-xs text-white hover:bg-white/5" data-testid="devis-blank-btn">
            <Plus size={14} />Devis vierge
          </button>
        </div>
        {pending.length === 0 ? (
          <div className="p-8 text-center text-sm text-white/50">Aucune demande en attente. 🎯</div>
        ) : (
          <ul className="divide-y divide-white/5">
            {pending.map((q) => (
              <li key={q.id} className="grid gap-3 px-5 py-4 md:grid-cols-12" data-testid={`pending-${q.id}`}>
                <div className="md:col-span-4">
                  <p className="text-xs uppercase tracking-wider text-white/40">{fmt(q.created_at)}</p>
                  <p className="mt-1 font-medium text-white">{q.name}</p>
                  <p className="text-xs text-white/50">{q.category}{q.service ? ` — ${q.service}` : ""}</p>
                </div>
                <div className="md:col-span-6">
                  <p className="text-xs uppercase tracking-wider text-white/40">Message</p>
                  <p className="mt-1 line-clamp-2 text-sm text-white/70">{q.message}</p>
                </div>
                <div className="flex items-start justify-end gap-2 md:col-span-2">
                  <button
                    onClick={() => setCreatingFor(q)}
                    className="inline-flex items-center gap-2 rounded-full bg-brand-gradient px-4 py-2 text-xs font-semibold text-white hover:scale-[1.02] transition-transform"
                    data-testid={`create-devis-${q.id}`}
                  >
                    <FileEdit size={12} />Créer devis
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="rounded-lg border border-white/10 bg-[#0f0f0f]">
        <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
          <div>
            <h3 className="flex items-center gap-2 font-head text-lg font-medium uppercase tracking-tight text-white"><FileText size={16} className="text-[#ff4d4d]" />Devis émis</h3>
            <p className="text-xs text-white/50">{items.length} devis</p>
          </div>
        </div>

        {loading ? (
          <div className="p-6 text-sm text-white/50">Chargement…</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center">
            <FileText size={32} className="mx-auto text-white/20" />
            <p className="mt-3 text-sm text-white/50">Aucun devis pour le moment.</p>
            <button onClick={() => setCreateOpen(true)} className="mt-5 rounded-full bg-brand-gradient px-5 py-2.5 text-xs font-semibold text-white">Créer le premier</button>
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {items.map((d) => {
              const meta = STATUS_META[d.status] || STATUS_META.brouillon;
              return (
                <div key={d.id} className="grid gap-3 px-5 py-4 md:grid-cols-12" data-testid={`devis-item-${d.id}`}>
                  <div className="md:col-span-3">
                    <p className="font-mono text-xs uppercase tracking-wider text-white/40">{d.number}</p>
                    <p className="mt-1 font-medium text-white">{d.client_name}</p>
                    <p className="text-xs text-white/50">{d.project || "—"}</p>
                  </div>
                  <div className="md:col-span-2 self-center">
                    <p className="text-xs uppercase tracking-wider text-white/40">Émis</p>
                    <p className="text-sm text-white">{fmt(d.created_at)}</p>
                  </div>
                  <div className="md:col-span-2 self-center">
                    <p className="text-xs uppercase tracking-wider text-white/40">Total TTC</p>
                    <p className="font-head text-lg font-semibold text-gradient">{money(d.total_ttc)}</p>
                  </div>
                  <div className="md:col-span-2 self-center">
                    <select
                      value={d.status}
                      onChange={(e) => setStatus(d.id, e.target.value)}
                      className={`block w-full rounded border px-2 py-1 text-xs ${meta.cls}`}
                      data-testid={`devis-status-${d.id}`}
                    >
                      {Object.keys(STATUS_META).map((k) => <option key={k} value={k} className="bg-black">{STATUS_META[k].label}</option>)}
                    </select>
                  </div>
                  <div className="flex flex-wrap items-center justify-end gap-2 md:col-span-3">
                    {d.client_email && (
                      <button onClick={() => sendEmail(d.id)} className="inline-flex items-center gap-1 rounded-md border border-white/10 px-3 py-2 text-xs text-white hover:bg-white/5" title="Envoyer par email" data-testid={`devis-send-${d.id}`}><Send size={12} />Email</button>
                    )}
                    <button onClick={() => downloadDevisPdf(d.id, d.number)} className="inline-flex items-center gap-1 rounded-md bg-brand-gradient px-3 py-2 text-xs font-semibold text-white" data-testid={`devis-download-${d.id}`}><Download size={12} />PDF</button>
                    <button onClick={() => remove(d.id)} className="rounded-md border border-red-500/30 px-2 py-2 text-red-400 hover:bg-red-500/10"><Trash2 size={12} /></button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {createOpen && (
        <DevisCreateModal open={createOpen} onOpenChange={setCreateOpen} onCreated={() => { setCreateOpen(false); load(); }} />
      )}
      {creatingFor && (
        <DevisCreateModal
          open={!!creatingFor}
          onOpenChange={(v) => !v && setCreatingFor(null)}
          prefill={{
            client_name: creatingFor.name, client_email: creatingFor.email,
            client_phone: creatingFor.phone,
            project: creatingFor.service || creatingFor.category, quote_id: creatingFor.id,
          }}
          onCreated={() => { setCreatingFor(null); load(); }}
        />
      )}
    </div>
  );
}
